import React from "react";
import { PreviewCard } from "@/components/DocumentMockups";

/* Hero document preview carousel — shows a continuous marquee of every
   document/add-on we ship inside a single Premium file (informational only,
   no selection / no CTA). Reuses the existing <PreviewCard> mockup. */

const PREVIEW_ITEMS = [
  { id: "visa",       title: "Visa Application Form",            package_contents: "Schengen short-stay (Type C)" },
  { id: "flight",     title: "Flight Reservation with valid PNR", package_contents: "Verifiable GDS booking" },
  { id: "hotel",      title: "Hotel Booking Confirmation",        package_contents: "Verified Booking.com reservation" },
  { id: "car",        title: "Car Rental Reservation",            package_contents: "Major rental agency" },
  { id: "cover",      title: "Cover Letter",                      package_contents: "Consulate-formatted" },
  { id: "itinerary",  title: "Travel Itinerary & Day-by-Day Plan", package_contents: "Detailed day-by-day plan" },
  { id: "insurance",  title: "Travel Insurance (€30,000)",        package_contents: "Schengen Area · 30 days" },
  { id: "noc",        title: "No Objection Certificate (NOC)",    package_contents: "Employer authorisation" },
  { id: "sponsor",    title: "Sponsor Letter",                    package_contents: "Financial sponsor declaration" },
  { id: "invitation", title: "Invitation Letter",                 package_contents: "Host invitation" },
];

export default function DocumentPreviewCarousel() {
  // duplicate the list once so the translateX(-50%) animation loops seamlessly
  const doubled = [...PREVIEW_ITEMS, ...PREVIEW_ITEMS];

  return (
    <div
      data-testid="doc-preview-carousel"
      className="w-full lg:w-[420px] bg-white text-[#0f1b2d] rounded-xl shadow-2xl border border-white/10 p-6 reveal overflow-hidden"
    >
      <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2563eb]">Sample of your file</div>
      <h3 className="mt-2 font-display font-bold text-xl text-[#0f1b2d]">Everything in your file — done for you</h3>
      <p className="mt-1 text-xs text-[#64748b]">10 documents shown · Premium package + all add-ons. Samples only — yours are personalised.</p>

      {/* Marquee viewport — internal overflow only, never spawns page scroll */}
      <div className="relative mt-5 -mx-6 px-6">
        {/* edge gradients to fade items in/out */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-6 z-10 bg-gradient-to-r from-white to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-6 z-10 bg-gradient-to-l from-white to-transparent" />

        <div className="overflow-hidden" data-testid="doc-preview-carousel-viewport">
          <div
            className="flex items-stretch gap-3 py-1"
            style={{
              width: "max-content",
              animationName: "schengenMarquee",
              animationDuration: "60s",
              animationTimingFunction: "linear",
              animationIterationCount: "infinite",
            }}
            data-testid="doc-preview-carousel-track"
          >
            {doubled.map((it, idx) => (
              <PreviewCard
                key={`${it.id}-${idx}`}
                item={{ title: it.title, package_contents: it.package_contents }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Footer caption — pulls attention back to the order CTA below */}
      <div className="mt-5 flex items-center justify-between text-[11px] text-[#64748b]">
        <div>
          <span className="font-semibold text-[#0f1b2d]">10</span> documents · 1 file
        </div>
        <div>Delivered in <span className="font-semibold text-[#0f1b2d]">24 hours</span> · Express 3h</div>
      </div>

      {/* Marquee keyframes (scoped via global stylesheet ok — animation name is namespaced) */}
      <style>{`
        @keyframes schengenMarquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="doc-preview-carousel-track"] {
            animation: none !important;
          }
        }
        [data-testid="doc-preview-carousel-track"]:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}
